import React, {
  memo,
  useMemo,
  lazy,
  Suspense,
  useState,
  useEffect,
} from "react";
import { Loading } from "components/shared";
import { useDispatch, useSelector } from "react-redux";
import {
  LAYOUT_TYPE_CLASSIC,
  LAYOUT_TYPE_MODERN,
  LAYOUT_TYPE_SIMPLE,
  LAYOUT_TYPE_STACKED_SIDE,
  LAYOUT_TYPE_DECKED,
  LAYOUT_TYPE_BLANK,
} from "constants/theme.constant";
import useAuth from "utils/hooks/useAuth";
import useDirection from "utils/hooks/useDirection";
import useLocale from "utils/hooks/useLocale";
import packageJson from "../../../package.json";
import { Button, Dialog } from "components/ui";
import { setCurrentVersion } from "store/base/commonSlice";

const layouts = {
  [LAYOUT_TYPE_CLASSIC]: lazy(() => import("./ClassicLayout")),
  [LAYOUT_TYPE_MODERN]: lazy(() => import("./ModernLayout")),
  [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import("./StackedSideLayout")),
  [LAYOUT_TYPE_SIMPLE]: lazy(() => import("./SimpleLayout")),
  [LAYOUT_TYPE_DECKED]: lazy(() => import("./DeckedLayout")),
  [LAYOUT_TYPE_BLANK]: lazy(() => import("./BlankLayout")),
};

const Layout = () => {
  const layoutType = useSelector((state) => state.theme.layout.type);
  // const currentVersion = useSelector((state) => state.base.common.version);

  const { authenticated } = useAuth();
  const dispatch = useDispatch();

  useDirection();
  useLocale();

  const [isCacheOld, setIsCacheOld] = useState(false);

  const AppLayout = useMemo(() => {
    if (!authenticated) {
      return lazy(() => import("./AuthLayout"));
    }
    return layouts[layoutType];
  }, [layoutType, authenticated]);

  const VERSION_LOCAL_KEY = "brainware_version";

  const clearBrowserCache = () => {
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    try {
      localStorage.removeItem("brainware");
      localStorage.setItem(VERSION_LOCAL_KEY, packageJson.version);
    } catch (e) {}

    dispatch(setCurrentVersion(packageJson.version));
    window.location.reload(true);
  };

  useEffect(() => {
    const saved = localStorage.getItem(VERSION_LOCAL_KEY);

    if (!saved) {
      localStorage.setItem(VERSION_LOCAL_KEY, packageJson.version);
      dispatch(setCurrentVersion(packageJson.version));
      return;
    }

    if (saved !== packageJson.version) {
      setIsCacheOld(true);
    }
  }, [dispatch]);

  return (
    <Suspense
      fallback={
        <div className="flex flex-auto h-screen">
          <Loading loading />
        </div>
      }
    >
      <AppLayout />

      <Dialog isOpen={isCacheOld}>
        <div className="px-2 pb-6">
          <h4 className="mb-4">Update Available</h4>
          <p>New version {packageJson.version} available. Please update.</p>
        </div>
        <div className="text-right px-6">
          <Button variant="solid" onClick={clearBrowserCache}>
            Update
          </Button>
        </div>
      </Dialog>
    </Suspense>
  );
};

export default memo(Layout);
